import React, { useId } from 'react'
import AppCustomEditor from 'src/utils/CustomEditor'
import { isDescriptionHtmlSource } from 'src/utils/descriptionHtmlSource'

export type DescriptionHtmlChangeOptions = {
  htmlSource?: boolean
}

type DescriptionHtmlEditorProps = {
  value: string
  onChange: (value: string, options?: DescriptionHtmlChangeOptions) => void
  htmlSource: boolean
  onHtmlSourceChange: (htmlSource: boolean) => void
  maxCharacters?: number | null
  placeholder?: string
}

const DescriptionHtmlEditor: React.FC<DescriptionHtmlEditorProps> = ({
  value,
  onChange,
  htmlSource,
  onHtmlSourceChange,
  maxCharacters,
  placeholder,
}) => {
  const sourceInputId = useId()
  const locksVisualEditor = htmlSource && isDescriptionHtmlSource(value)
  const charactersOverLimit =
    htmlSource && maxCharacters != null && maxCharacters > 0
      ? value.length - maxCharacters
      : 0

  return (
    <div>
      <div className='form-check form-switch mb-2'>
        <input
          className='form-check-input'
          type='checkbox'
          role='switch'
          id={sourceInputId}
          checked={htmlSource}
          disabled={locksVisualEditor}
          title={
            locksVisualEditor
              ? 'HTML templates stay in HTML source so the editor does not add extra tags.'
              : 'Save this description as HTML source.'
          }
          onChange={(event) => onHtmlSourceChange(event.target.checked)}
        />
        <label className='form-check-label' htmlFor={sourceInputId}>
          HTML source
        </label>
      </div>
      {htmlSource ? (
        <textarea
          className='form-control'
          rows={12}
          value={value}
          spellCheck={false}
          style={{ fontFamily: 'Consolas, monospace' }}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value, { htmlSource: true })}
        />
      ) : (
        <AppCustomEditor
          value={value}
          onChangeData={(nextValue) => onChange(nextValue, { htmlSource: false })}
          placeholder={placeholder}
          onHtmlDocumentPaste={(html) => {
            onHtmlSourceChange(true)
            onChange(html, { htmlSource: true })
          }}
        />
      )}
      {charactersOverLimit > 0 ? (
        <small className='text-danger d-block mt-1'>
          This HTML is {charactersOverLimit} characters over the recommended limit and will be
          saved in full.
        </small>
      ) : null}
    </div>
  )
}

export default DescriptionHtmlEditor
